import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { useQueryParams } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  ChallengesQuery,
  ChallengesQueryVariables,
} from './__generated__/useChallenges';
import { queryParams } from './queryParams';

const CHALLENGES_QUERY = gql`
  query Challenges(
    $name: String
    $challengeUUID: [String!]
    $status: [ChallengeStatus!]
    $challengeSystem: ChallengeExternalSystem
    $after: String
    $before: String
    $desc: Boolean
    $first: Int
  ) {
    viewer {
      id
      challenges: searchChallengesV2(
        challengeNamePrefix: $name
        challengeIdList: $challengeUUID
        status: $status
        challengeSystem: $challengeSystem
        after: $after
        before: $before
        desc: $desc
        first: $first
      ) {
        edges {
          node {
            challengeUUID
            challengeId
            name
            numberOfPlayers
            status
            startDate
            endDate
            countries
            brands
            challengeActions
            challengeSystem
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
`;

export default function useChallenges() {
  const [query, setQuery] = useQueryParams(queryParams);

  const status = useMemo(() => {
    if (query.status) {
      return Array.isArray(query.status) ? query.status : [query.status];
    }
    return [];
  }, [query.status]);

  const [{ data, fetching }, refresh] = useQuery<
    ChallengesQuery,
    ChallengesQueryVariables
  >({
    query: CHALLENGES_QUERY,
    variables: {
      first: 20,
      ...mapVariables(query),
      status,
    },
  });

  return {
    refresh,
    fetching,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(query, setQuery, data?.viewer.challenges?.pageInfo),
    challenges: data?.viewer.challenges?.edges?.map((edge) => edge?.node),
  };
}
